import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X } from '@styled-icons/fa-solid';
import { Spinner } from 'react-bootstrap';

const FilterableSelect = ({
  onChange,
  disabled,
  required,
  onChangeAsObject,
  className,
  label,
  smallText,
  data,
  setData,
  options,
  info,
  error,
  name,
  isLoading,
  onSearchChange,
  onClear,
  onDropdownOpen,
  dataTestId,
}) => {
  const [search, setSearch] = useState();
  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownAreaRef = useRef();
  const buttonRef = useRef();
  const searchRef = useRef();

  const outsideClickListener = (event) => {
    if (!dropdownAreaRef.current || !buttonRef.current) return;
    if (!dropdownAreaRef.current.contains(event.target) && !buttonRef.current.contains(event.target)) {
      setShowDropdown(false);
      setSearch('');
      removeClickListener();
    }
  };

  const removeClickListener = () => {
    document.removeEventListener('click', outsideClickListener);
  };
  const clickButton = () => {
    if (!disabled) {
      if (!showDropdown) {
        onDropdownOpen?.();
        setShowDropdown(true);
        document.addEventListener('click', outsideClickListener);
      } else {
        setShowDropdown(false);
        setSearch('');
        removeClickListener();
      }
    }
  };

  useEffect(() => {
    if (showDropdown) {
      searchRef.current.focus();
      if (onDropdownOpen) {
        onDropdownOpen();
      }
    }
  }, [showDropdown]);

  const handleChange = (data) => {
    setShowDropdown(false);
    removeClickListener();
    setData?.(data);
    onChange?.(data);

    const selected = options.find((opt) => opt?.id?.toString?.() === data);
    onChangeAsObject?.(selected);
  };

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return options
      .filter((opt) => {
        return opt.data?.toUpperCase?.().indexOf(search?.toUpperCase()) > -1;
      })
      .sort((a, b) => {
        if (
          a.data?.toUpperCase?.()?.startsWith(search?.toUpperCase()) &&
          !b.data?.toUpperCase?.()?.startsWith(search?.toUpperCase())
        )
          return -1;
        return 0;
      });
  }, [options, search]);

  const OptionsList = ({ options, handleChange }) => {
    let result = [];

    options.forEach((opt) => {
      result.push(
        <option
          onClick={() => {
            handleChange(opt.id);
          }}
          className="input-filterable-option"
          key={opt.id}
          value={opt.id}
          style={{ color: 'black' }}
          tabIndex="0"
          onKeyDown={(e) => {
            if (['Enter', ' '].includes(e.key)) {
              handleChange(opt.id);
              e.preventDefault();
            }
          }}
        >
          {opt.data}
        </option>
      );
    });

    return result;
  };

  const SelectOptionsList = ({ options }) => {
    let result = [];
    result.push(
      <option key={0} value="">
        - Select -
      </option>
    );

    options.forEach((opt) => {
      result.push(
        <option key={opt.id} value={opt.id} style={{ color: 'black' }}>
          {opt.data}
        </option>
      );
    });

    return result;
  };

  const displayValue = useMemo(() => {
    const value = {
      text: options.find((opt) => opt?.id?.toString?.() === data?.toString())?.data,
      none: !options.find((opt) => opt?.id?.toString?.() === data?.toString()),
    };
    return value;
  }, [data, options]);

  return (
    <div className={`input-outer-container ${className || ''}`}>
      <label className="input-label label-text">
        <span>{label}</span>
        <span className="startInputs">{smallText}</span>
        <span style={{ flexGrow: 1 }}></span>
        <span className="input-error-box">{error || '\u00A0'}</span>
        <span>{typeof info === 'function' ? info() : (info ?? '')}</span>
      </label>
      <div className="input-filterable-dropdown">
        <select
          ref={buttonRef}
          name={name}
          data-testid={dataTestId}
          required={required}
          className={
            'd-flex flex-row justify-content-between input-filterable-button ' +
            (disabled ? 'input-filterable-button-disabled' : '')
          }
          style={displayValue.none ? { color: '#959595' } : { color: 'black' }}
          value={data}
          onMouseDown={(e) => {
            e.preventDefault();
            clickButton();
          }}
          onKeyDown={(e) => {
            if (!['Tab', 'Shift', 'Control', 'Alt'].includes(e.key)) {
              e.preventDefault();
              clickButton();
            }
          }}
        >
          <SelectOptionsList options={options} />
        </select>

        {onClear && (
          <div
            style={{
              position: 'absolute',
              right: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
            }}
            onClick={onClear}
          >
            <X width="1rem" height="1rem" style={{ color: 'grey', backgroundColor: 'white' }} />
          </div>
        )}

        <div
          ref={dropdownAreaRef}
          className="input-filterable-dropdown-content"
          style={showDropdown ? { display: 'flex' } : { display: 'none' }}
        >
          <div className="input-filterable-search-container">
            <Search
              width="1rem"
              style={{
                color: 'grey',
                backgroundColor: 'white',
                marginLeft: '4px',
              }}
            />
            <input
              ref={searchRef}
              onChange={(e) => {
                setSearch(e.target.value);
                if (onSearchChange) {
                  onSearchChange(e.target.value);
                }
              }}
              value={search}
              type="text"
              placeholder="Search.."
              className="input-filterable-search"
              onKeyDown={(e) => {
                if (e.key === 'Escape') clickButton();
              }}
            />
          </div>
          <div className="input-filterable-options-container">
            {isLoading ? (
              <div className="py-2 text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <OptionsList options={filteredOptions} handleChange={handleChange} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterableSelect;
